import { useCallback, useEffect, useId, useRef } from 'react';
import type { z } from 'zod';

import { useLogger } from '@archipro-website/logger/client';
import { useTracker } from '@archipro-website/tracker';
import {
    assertErrorResponse,
    assertSuccessResponse,
} from '@modules/root/graphql/responses';
import { useUserLoggedIn } from '@modules/user';
import { trackSubmitCreateEnquiryRequest } from '@modules/tracking/util/trackSubmitCreateEnquiryRequest';
import { trackBrochureRequest } from '@modules/tracking/util/trackBrochureRequest';
import { trackProjectEnquire } from '@modules/tracking/util/trackProjectEnquiry';
import { fileStorage } from '~/utils/file-storage';
import { enquiryFormSessionSchema } from '../validation';
import { useEnquiryEvent, useEnquirySession } from './use-enquiry';
import { useIsSocialLoginCallback } from '~/modules/root/hook/use-is-social-logic-callback';
import { XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { trackCreatedEnquiry } from '~/modules/tracking/util/trackCreatedEnquiry';

/**
 * Hook to enable enquiry form auto-submission after login. This hook should
 * only be called once in the component tree near the root component.
 * @see {@link packages/web/app/modules-rocco/enquiry/hooks/use-enquiry.ts}
 */
export function useEnquiryAutoSubmit() {
    const id = useId();
    const { isLoggedIn, user } = useUserLoggedIn();
    const logger = useLogger();
    const tracker = useTracker();
    const isSocialLoginCallback = useIsSocialLoginCallback(true);
    // Form calls this to register latest file attachments
    const filesRef = useRef<File[]>([]);
    const {
        fetcher,
        enquirySession,
        enquiryFormId,
        enquiryAutoSubmit,
        autoSubmitDelay,
        isAutoSubmitted,
        setEnquirySession,
        setEnquiryFormId,
        setIsAutoSubmitted,
        setEnquiryAutoSubmit,
    } = useEnquirySession();

    const onSuccess = useCallback(() => {
        if (!isAutoSubmitted) {
            return;
        }
        trackCreatedEnquiry(tracker, {
            source: enquirySession?.source ?? 'InPage',
        });
        // The user is already logged in at this stage, don't need to track
        // the auth event.
        setEnquirySession((session) => ({ ...session, message: '' }));
        setEnquiryAutoSubmit(false);
        toast.success('Your enquiry was submitted', {
            cancel: {
                label: <XIcon className="size-4" />,
                onClick: () => {},
            },
            cancelButtonStyle: {
                backgroundColor: 'transparent',
                minWidth: 0,
            },
        });
    }, [
        enquirySession?.source,
        isAutoSubmitted,
        setEnquiryAutoSubmit,
        setEnquirySession,
        tracker,
    ]);
    const { triggerSuccess } = useEnquiryEvent({ onSuccess });

    /**
     * Similar to `submitEnquiry` in `useEnquiry`.
     * @see {@link ./use-enquiry.ts}
     */
    const autoSubmitEnquiry = useCallback(
        (values: z.infer<typeof enquiryFormSessionSchema>) => {
            // Prevent duplicate submissions if already submitting.
            if (fetcher.state !== 'idle') {
                return;
            }
            if (values.isModal) {
                // If the in-page form was submitted, use the in-page form
                // post-submission logic to show the success animation.
                // Otherwise, use the auto-submit post-submission logic.
                setEnquiryFormId(id);
            }
            setIsAutoSubmitted(true);
            setEnquiryAutoSubmit(false);
            const payload = {
                name: values.name,
                email: isLoggedIn && user.Email ? user.Email : values.email,
                phoneNumber: values.phoneNumber,
                suburbPostcode: values.postcode.value?.region ?? '',
                locationResponse: JSON.stringify(values.postcode.value),
                branchId: values.branchId ?? null,
                message: values.message,
                siteTreeId: values.siteTreeId,
                targetProfessionalId: values.professional.ID,
                shareDetails: 'true',
            };
            const formData = new FormData();
            for (const [key, value] of Object.entries(payload)) {
                if (value !== undefined && value !== null) {
                    formData.append(key, String(value));
                }
            }

            // Add files to FormData if they exist
            if (filesRef.current.length > 0) {
                filesRef.current.forEach((file) => {
                    formData.append('Attachments[]', file);
                });
            }

            fetcher.submit(formData, {
                method: 'POST',
                action: '/remix-api/enquiry/submit',
                encType: 'multipart/form-data',
            });

            tracker.log('EnquirySubmit', {
                url: new URL(window.location.href),
                label: values.professional.Title,
            });
            trackSubmitCreateEnquiryRequest(tracker, {
                source: values.source,
                isAutoSubmit: true,
            });
            if (
                values.itemType === 'professional' ||
                values.itemType === 'product'
            ) {
                trackBrochureRequest(tracker, {
                    type: values.itemType,
                    region: payload.suburbPostcode,
                });
            } else if (values.itemType === 'project' && values.relatedItem) {
                trackProjectEnquire(tracker, {
                    category: values.relatedItem.category,
                    step: 2,
                    project: values.relatedItem.title,
                    professional: values.professional.Title,
                    region: payload.suburbPostcode,
                });
            }
            if (values.source === 'InPage') {
                tracker.log('OnPageEnquirySend', {
                    url: new URL(window.location.href),
                    targetTracker: 'archiproTracker',
                });
            }
        },
        [
            id,
            isLoggedIn,
            user,
            fetcher,
            tracker,
            setEnquiryFormId,
            setIsAutoSubmitted,
            setEnquiryAutoSubmit,
        ]
    );

    // Automatically submit enquiry from saved session after login.
    useEffect(() => {
        if (
            !enquiryAutoSubmit ||
            !enquirySession ||
            !isLoggedIn ||
            autoSubmitDelay ||
            isSocialLoginCallback ||
            fetcher.state !== 'idle'
        ) {
            return;
        }
        // Prevent duplicate submission race conditions.
        setEnquiryAutoSubmit(false);

        async function tryAutoSubmit() {
            const result = enquiryFormSessionSchema.safeParse(enquirySession);
            if (!result.success) {
                return;
            }

            // Restore files from IndexedDB before submitting
            const sessionId = `${result.data.professional.ID}-${result.data.siteTreeId}`;
            const files = await fileStorage.getFiles(sessionId);
            filesRef.current = files; // Attach files for formData.append()
            autoSubmitEnquiry(result.data);
        }

        void tryAutoSubmit();
    }, [
        isLoggedIn,
        enquirySession,
        enquiryAutoSubmit,
        autoSubmitDelay,
        isSocialLoginCallback,
        fetcher.state,
        autoSubmitEnquiry,
        setEnquiryAutoSubmit,
    ]);

    // Similar to the `useEffect` in `useEnquiry`.
    const isHandleSubmitComplete = useRef(true);
    useEffect(() => {
        if (enquiryFormId !== id) {
            // Only run if the form was auto-submitted.
            return;
        } else if (fetcher.state !== 'idle') {
            // Ensure this is only run once per submission.
            isHandleSubmitComplete.current = false;
            return;
        } else if (isHandleSubmitComplete.current || !fetcher.data) {
            return;
        }
        const isSuccess = assertSuccessResponse(fetcher.data);
        const isError = assertErrorResponse(fetcher.data);
        if (isSuccess) {
            triggerSuccess();
        } else if (isError) {
            setIsAutoSubmitted(false);
            logger.error('Enquiry auto-submit failed', {
                error: fetcher.data,
            });
        }
        isHandleSubmitComplete.current = true;
    }, [
        id,
        fetcher,
        logger,
        tracker,
        enquirySession,
        enquiryFormId,
        setEnquirySession,
        setEnquiryAutoSubmit,
        triggerSuccess,
        setIsAutoSubmitted,
    ]);
}
